/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import ArgonButton from "components/ArgonButton";
import {
  FormControl,
  Select,
  LinearProgress,
  MenuItem,
  InputLabel,
  Chip,
  Button,
} from "@mui/material";
import { Person, Refresh, FilterList } from "@mui/icons-material";

import breakpoints from "assets/theme/base/breakpoints";

// Data
// import authorsTableData from "layouts/tables/data/authorsTableData";
//import ParentsTableData from "Views/people/parents/data/parentsTableData";
import projectsTableData from "layouts/tables/data/projectsTableData";
import ClassMgtTableData from "Views/Activities/ClassMgt/data/classMgtTableData";
import EditAttendanceModal from "Views/Activities/AllAttendance/components/EditAttendanceModal";
import MarkAttendanceModal from "Views/Activities/AllAttendance/components/MarkAttendanceModal";
import ShortListTutorsTableData from "Views/Activities/ShortListTutors/data/ShortListTableData";
import AttendanceListViewTutorSummaryTableData from "Views/Activities/AllAttendance/data/AttendanceListViewTutorSummaryTableData";

import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Input } from "@mui/material";
import { Add, AddCircle as AddCircleIcon } from "@mui/icons-material";

// import ReferralTableData from "Views/Activities/Referral/data/ReferralTableData"
import { useState, useEffect } from "react";
import Dropdown from "components/CustomDropdown/Dropdown";
import ArgonInput from "components/ArgonInput";
import { useParams } from "react-router-dom";
import api from "utils/api";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";

function Tables() {
  const { id: classId } = useParams();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [attendanceList, setAttendanceList] = useState([]);
  const [filteredAttendanceList, setFilteredAttendanceList] = useState([]);
  const [classDetails, setClassDetails] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [stats, setStats] = useState({ total: 0, regular: 0, extra: 0, recovery: 0 });

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [attendanceToDelete, setAttendanceToDelete] = useState(null);

  // Mark Attendance Modal States
  const [markAttendanceOpen, setMarkAttendanceOpen] = useState(false);
  const [submittingAttendance, setSubmittingAttendance] = useState(false);

  const handleDeleteClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Real data from API
  const [data, setData] = useState({
    completedSessions: 0,
    totalSessions: 0,
    remainingSessions: 0,
    extraClasses: 0,
    recoveryClasses: 0,
    startDate: "N/A",
    endDate: "N/A",
    classStatus: "N/A",
    cyclePaymentStatus: "PENDING",
    remark: "",
  });

  // Cycle-wise data
  const [cycles, setCycles] = useState([]);
  const [cyclesFullData, setCyclesFullData] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState("");
  const [cycleData, setCycleData] = useState(null);

  // Fetch attendance data by class ID
  const fetchAttendanceData = async () => {
    if (!classId) {
      toast.error("Class ID not found");
      return;
    }

    setLoading(true);
    try {
      const response = await api.get("/attendance", {
        params: { classSessionId: classId },
      });

      if (response.data.success) {
        const attendances = response.data.data; // Changed: attendances is now response.data.data

        const normalizedAttendances = attendances || [];

        setAttendanceList(normalizedAttendances);
        // Extract classSession from the first attendance if available
        const firstAttendance = normalizedAttendances[0];
        const classSession = firstAttendance?.classSession || null;

        // Set classDetails with students from classSession
        if (classSession) {
          setClassDetails(classSession);

          // Attach classSession to all attendances if not already present
          normalizedAttendances.forEach((attendance) => {
            if (!attendance.classSession) {
              attendance.classSession = classSession;
            }
          });
        }

        const computedStats = {
          total: normalizedAttendances.length,
          regular: normalizedAttendances.filter((a) =>
            Array.isArray(a.type) ? a.type.includes("REGULAR") : a.type === "REGULAR"
          ).length,
          extra: normalizedAttendances.filter((a) =>
            Array.isArray(a.type) ? a.type.includes("EXTRA") : a.type === "EXTRA"
          ).length,
          recovery: normalizedAttendances.filter((a) =>
            Array.isArray(a.type) ? a.type.includes("RECOVERY") : a.type === "RECOVERY"
          ).length,
        };

        setStats(computedStats);

        if (student || cycle) {
          applyFilters(student, cycle, normalizedAttendances);
        } else {
          setFilteredAttendanceList([...normalizedAttendances]);
        }

        const uniqueSessions = new Set(
          normalizedAttendances
            .filter((a) => a.attendanceTime)
            .map((a) => new Date(a.attendanceTime).toDateString())
        );

        const completedClasses = classSession?.completedClasses ?? uniqueSessions.size;
        const totalClasses = classSession?.totalNoOfClasses ?? completedClasses;
        const extraClasses = classSession?.extraClasses ?? computedStats.extra;
        const recoveryClasses = classSession?.recoveryClasses ?? computedStats.recovery;
        const remainingClasses = Math.max(0, totalClasses - completedClasses);

        const sortedAttendances = normalizedAttendances
          .filter((attendance) => attendance.attendanceTime)
          .slice()
          .sort(
            (a, b) => new Date(a.attendanceTime).getTime() - new Date(b.attendanceTime).getTime()
          );

        const firstAttendanceSorted = sortedAttendances[0];
        const lastAttendance = sortedAttendances[sortedAttendances.length - 1];

        setData({
          completedSessions: completedClasses,
          totalSessions: totalClasses,
          remainingSessions: remainingClasses,
          extraClasses,
          recoveryClasses,
          startDate: firstAttendanceSorted
            ? new Date(firstAttendanceSorted.attendanceTime).toLocaleDateString("en-IN")
            : "N/A",
          endDate: lastAttendance
            ? new Date(lastAttendance.attendanceTime).toLocaleDateString("en-IN")
            : "N/A",
          classStatus: classSession?.status || "CREATED",
          cyclePaymentStatus: classSession?.paymentStatus || "PENDING",
          remark:
            totalClasses === 0
              ? "Total classes not set. Please update class session details."
              : "Attendance tracking in progress",
        });
      }
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      toast.error(error.response?.data?.message || "Failed to fetch attendance data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch cycle-wise attendance data and select the current (ongoing) cycle by default
  const fetchCycleWiseData = async () => {
    if (!classId) {
      toast.error("Class ID not found");
      return;
    }

    try {
      const response = await api.get("/attendance/cycle-wise", {
        params: { classSessionId: classId },
      });

      if (response.data.success) {
        const classSessionData = response.data.data;
        const cyclesData = classSessionData.attendance || [];

        // Store full cycle data
        setCyclesFullData(cyclesData);

        // Extract cycle names for dropdown
        const cycleNames = cyclesData.map((c) => c.cycleName);
        setCycles(cycleNames);

        // Find the current (ongoing) cycle
        let defaultCycle = "";
        const ongoingCycle = cyclesData.find(
          (c) => (c.cycleInfo?.status || c.status) === "ONGOING"
        );
        if (ongoingCycle) {
          defaultCycle = ongoingCycle.cycleName;
        } else if (cycleNames.length > 0) {
          defaultCycle = cycleNames[0];
        }

        // Set selectedCycle to the ongoing cycle (or first if none ongoing)
        if (defaultCycle && !selectedCycle) {
          setSelectedCycle(defaultCycle);
          // Also fetch and show data for this cycle immediately
          handleCycleChange(defaultCycle);
        }
      }
    } catch (error) {
      console.error("Error fetching cycle-wise data:", error);
      toast.error(error.response?.data?.message || "Failed to fetch cycle data");
    }
  };

  // Update data state from selected cycle
  const updateDataFromCycle = (cycleInfo, sessionData = null) => {
    if (!cycleInfo || !cycleInfo.cycleInfo) {
      return;
    }

    const cycle = cycleInfo.cycleInfo;
    const attendances = cycleInfo.attendance || [];

    // Get start and end dates from cycle attendances - filter out only completed ones
    const completedAttendances = attendances.filter(
      (a) => a.attendanceTime && new Date(a.attendanceTime) <= new Date()
    );

    const sortedAttendances = completedAttendances
      .slice()
      .sort((a, b) => new Date(a.attendanceTime) - new Date(b.attendanceTime));

    const startDate =
      sortedAttendances.length > 0
        ? new Date(sortedAttendances[0].attendanceTime).toLocaleDateString("en-IN")
        : "N/A";

    const endDate =
      sortedAttendances.length > 0
        ? new Date(
            sortedAttendances[sortedAttendances.length - 1].attendanceTime
          ).toLocaleDateString("en-IN")
        : "N/A";

    setData({
      completedSessions: cycle.completedClasses || 0,
      totalSessions: cycle.totalClasses || 0,
      remainingSessions: Math.max(0, (cycle.totalClasses || 0) - (cycle.completedClasses || 0)),
      extraClasses: cycle.extraClasses || 0,
      recoveryClasses: cycle.recoveryClasses || 0,
      startDate: startDate,
      endDate: endDate,
      classStatus: (sessionData || classDetails)?.status || cycle.status || "N/A",
      cyclePaymentStatus: cycle.paymentStatus || "PENDING",
      remark: `${cycleInfo.cycleName} - ${cycle.completedClasses || 0}/${
        cycle.totalClasses || 0
      } classes completed`,
    });

    // Update filtered attendance list to show cycle's attendances
    setFilteredAttendanceList(attendances);
  };

  // Handle cycle change
  const handleCycleChange = async (cycleName) => {
    if (!cycleName) {
      // If empty/cleared, show all attendance
      await fetchAttendanceData();
      return;
    }

    setSelectedCycle(cycleName);
    setLoading(true);

    try {
      const response = await api.get("/attendance/by-cycle", {
        params: {
          classSessionId: classId,
          cycle: cycleName,
        },
      });

      if (response.data.success) {
        const cycleInfo = response.data.data;
        setCycleData(cycleInfo);

        // Normalize attendances for table (ensure all required fields)
        let attendances = [];
        if (Array.isArray(cycleInfo.attendances)) {
          attendances = cycleInfo.attendances;
        } else if (Array.isArray(cycleInfo.attendance)) {
          attendances = cycleInfo.attendance;
        }

        const cycle = cycleInfo.cycle || cycleInfo.cycleInfo || {};
        const sessionData = cycleInfo.classSession;

        // Attach classSession to all attendances if not already present
        if (sessionData) {
          attendances.forEach((attendance) => {
            if (!attendance.classSession) {
              attendance.classSession = sessionData;
            }
          });
        }

        // Get start and end dates from all attendances in cycle
        const sortedAttendances = attendances
          .filter((a) => a.attendanceTime)
          .slice()
          .sort((a, b) => new Date(a.attendanceTime) - new Date(b.attendanceTime));

        const startDate =
          sortedAttendances.length > 0
            ? new Date(sortedAttendances[0].attendanceTime).toLocaleDateString("en-IN")
            : "N/A";

        const endDate =
          sortedAttendances.length > 0
            ? new Date(
                sortedAttendances[sortedAttendances.length - 1].attendanceTime
              ).toLocaleDateString("en-IN")
            : "N/A";

        setData({
          completedSessions: cycle.progress?.completed || cycle.completedClasses || 0,
          totalSessions: cycle.progress?.total || cycle.totalClasses || 0,
          remainingSessions:
            cycle.progress?.remaining ||
            Math.max(
              0,
              (cycle.progress?.total || cycle.totalClasses || 0) -
                (cycle.progress?.completed || cycle.completedClasses || 0)
            ),
          extraClasses: cycle.extraClasses || 0,
          recoveryClasses: cycle.recoveryClasses || 0,
          startDate: startDate,
          endDate: endDate,
          classStatus: sessionData?.status || cycle.status || "N/A",
          cyclePaymentStatus: cycle.paymentStatus || "PENDING",
          remark: `${cycleName} - ${cycle.progress?.completed || cycle.completedClasses || 0}/${
            cycle.progress?.total || cycle.totalClasses || 0
          } classes completed`,
        });

        // Update filtered attendance list with new array to trigger re-render
        setFilteredAttendanceList([...attendances]);

        // Update stats
        const computedStats = {
          total: attendances.length,
          regular: attendances.filter((a) =>
            Array.isArray(a.type) ? a.type.includes("REGULAR") : a.type === "REGULAR"
          ).length,
          extra: attendances.filter((a) =>
            Array.isArray(a.type) ? a.type.includes("EXTRA") : a.type === "EXTRA"
          ).length,
          recovery: attendances.filter((a) =>
            Array.isArray(a.type) ? a.type.includes("RECOVERY") : a.type === "RECOVERY"
          ).length,
        };
        setStats(computedStats);
      }
    } catch (error) {
      console.error("Error fetching cycle data:", error);
      toast.error(error.response?.data?.message || "Failed to fetch cycle data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    const loadData = async () => {
      // Load all attendance first
      await fetchAttendanceData();
      // Then load cycle list for dropdown
      await fetchCycleWiseData();
    };

    loadData();
  }, [classId]);

  const progress = data.totalSessions > 0 ? (data.completedSessions / data.totalSessions) * 100 : 0;

  // Function to get payment status color
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "PAID":
        return {
          background: "linear-gradient(45deg, #4CAF50, #45a049)",
          boxShadow: "0 4px 15px rgba(76, 175, 80, 0.3)",
        };
      case "APPROVED":
        return {
          background: "linear-gradient(45deg, #2196F3, #1976D2)",
          boxShadow: "0 4px 15px rgba(33, 150, 243, 0.3)",
        };
      case "PENDING":
        return {
          background: "linear-gradient(45deg, #FF9800, #F57C00)",
          boxShadow: "0 4px 15px rgba(255, 152, 0, 0.3)",
        };
      case "REJECTED":
        return {
          background: "linear-gradient(45deg, #f44336, #d32f2f)",
          boxShadow: "0 4px 15px rgba(244, 67, 54, 0.3)",
        };
      case "FAILED":
        return {
          background: "linear-gradient(45deg, #9E9E9E, #757575)",
          boxShadow: "0 4px 15px rgba(158, 158, 158, 0.3)",
        };
      default:
        return {
          background: "linear-gradient(45deg, #FF9800, #F57C00)",
          boxShadow: "0 4px 15px rgba(255, 152, 0, 0.3)",
        };
    }
  };

  const [student, setStudent] = useState("");
  const [cycle, setCycle] = useState("");

  const studentOptions = (classDetails?.students || []).map((studentItem, index) => {
    const rawId = studentItem.id || studentItem._id || studentItem?.studentId;
    const value = rawId ? String(rawId) : `student-${index}`;

    return {
      label: studentItem.name || studentItem.email || `Student ${index + 1}`,
      value,
    };
  });

  const cycleOptions = Array.from(
    new Set(attendanceList.flatMap((attendance) => attendance.type).filter(Boolean))
  );

  const applyFilters = (selectedStudentId, selectedCycleType, source = attendanceList) => {
    const normalizedStudentId = selectedStudentId ? String(selectedStudentId) : "";
    const normalizedCycleType = selectedCycleType || "";

    const filtered = source.filter((attendance) => {
      const matchesStudent =
        !normalizedStudentId ||
        (attendance.classSession?.students || []).some((studentItem, index) => {
          const identifier = studentItem.id || studentItem._id || studentItem?.studentId;
          const fallbackIdentifier = `student-${index}`;

          return (
            String(identifier || "") === normalizedStudentId ||
            fallbackIdentifier === normalizedStudentId
          );
        });

      const matchesCycle =
        !normalizedCycleType ||
        (Array.isArray(attendance.type)
          ? attendance.type.includes(normalizedCycleType)
          : attendance.type === normalizedCycleType);

      return matchesStudent && matchesCycle;
    });

    setFilteredAttendanceList(filtered);
  };

  const handleStudentFilterChange = (value) => {
    setStudent(value);
    applyFilters(value, cycle);
  };

  const handleCycleFilterChange = (value) => {
    setCycle(value);
    applyFilters(student, value);
  };

  const handleResetFilters = () => {
    setStudent("");
    setCycle("");
    setFilteredAttendanceList(attendanceList);
  };

  // Edit click handler
  const handleEditClick = (attendance) => {
    setSelectedAttendance(attendance);
    setEditModalOpen(true);
  };

  // // Delete click handler
  const handleAttendanceDeleteClick = (attendance) => {
    setAttendanceToDelete(attendance);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (!attendanceToDelete) return;

    try {
      setLoading(true);
      const response = await api.delete(`/attendance/${attendanceToDelete.id}`);

      if (response.data.success) {
        toast.success("Attendance deleted successfully");
        // Refresh the attendance list
        fetchAttendanceData();
        setDeleteDialogOpen(false);
        setAttendanceToDelete(null);
      } else {
        toast.error(response.data.message || "Failed to delete attendance");
      }
    } catch (error) {
      console.error("Error deleting attendance:", error);
      toast.error(error.response?.data?.message || "Failed to delete attendance");
    } finally {
      setLoading(false);
    }
  };

  // Close delete dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setAttendanceToDelete(null);
  };

  // Close edit modal
  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedAttendance(null);
  };

  // Handle attendance update
  const handleAttendanceUpdate = () => {
    fetchAttendanceData();
  };

  // Mark Attendance handlers
  const handleMarkAttendanceOpen = () => {
    setMarkAttendanceOpen(true);
  };

  const handleMarkAttendanceClose = () => {
    setMarkAttendanceOpen(false);
  };

  const handleSubmitMarkAttendance = async (formData) => {
    // Check if this is a leave submission
    if (formData.isLeave) {
      // Handle leave marking with different API
      if (!formData.dates || formData.dates.length === 0) {
        toast.error("Please select at least one date");
        return;
      }

      if (!formData.leaveFor) {
        toast.error("Please select Leave For");
        return;
      }

      setSubmittingAttendance(true);
      try {
        const payload = {
          classSessionId: formData.classSessionId,
          dates: formData.dates,
          leaveFor: formData.leaveFor,
        };

        console.log("Submitting leave:", payload);

        const response = await api.post("/attendance/mark-leave", payload);

        if (response.data.success) {
          toast.success(`Leave marked successfully for ${formData.dates.length} date(s)`);
          handleMarkAttendanceClose();
          fetchAttendanceData();
          if (selectedCycle) {
            handleCycleChange(selectedCycle);
          }
        }
      } catch (error) {
        console.error("Error marking leave:", error);
        toast.error(error.response?.data?.message || "Failed to mark leave");
      } finally {
        setSubmittingAttendance(false);
      }
      return;
    }

    // Handle regular attendance marking
    if (!formData.topicCovered) {
      toast.error("Please enter topic covered");
      return;
    }

    if (!formData.attendanceId) {
      toast.error("Please select a date");
      return;
    }

    setSubmittingAttendance(true);
    try {
      const payload = new FormData();
      payload.append("attendanceTime", formData.attendanceTime);
      payload.append("attendanceId", formData.attendanceId);

      if (formData.topicCovered) {
        payload.append("topicCovered", formData.topicCovered);
      }

      if (formData.homeWork) {
        payload.append("homeWork", formData.homeWork);
      }

      if (formData.voiceNote) {
        payload.append("voiceNote", formData.voiceNote);
      }

      console.log("Submitting attendance:");
      for (let pair of payload.entries()) {
        console.log(pair[0], pair[1]);
      }

      const response = await api.post("/attendance", payload, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        toast.success("Attendance marked successfully");
        handleMarkAttendanceClose();
        fetchAttendanceData();
        if (selectedCycle) {
          handleCycleChange(selectedCycle);
        }
      }
    } catch (error) {
      console.error("Error marking attendance:", error);
      toast.error(error.response?.data?.message || "Failed to mark attendance");
    } finally {
      setSubmittingAttendance(false);
    }
  };

  const { columns, rows } = AttendanceListViewTutorSummaryTableData(
    filteredAttendanceList,
    handleEditClick,
    handleAttendanceDeleteClick
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                Attendance
              </ArgonTypography>

              {/* Filter Controls */}
              <ArgonBox
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "16px",
                  p: 3,
                  border: "1px solid #e0e0e0",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  mb: 3,
                }}
              >
                <ArgonBox
                  display="flex"
                  gap={3}
                  flexWrap="wrap"
                  sx={{
                    alignItems: { xs: "stretch", sm: "flex-end" },
                  }}
                >
                  {/* Cycle Dropdown */}
                  <ArgonBox>
                    <ArgonTypography
                      variant="body2"
                      fontWeight="medium"
                      color="text.primary"
                      mb={1}
                    >
                      Select Cycle
                    </ArgonTypography>
                    <Dropdown
                      label=""
                      value={selectedCycle}
                      options={cycles.map((cycleName) => {
                        const cycleFullData = cyclesFullData.find((c) => c.cycleName === cycleName);
                        const cycleStatus =
                          cycleFullData?.cycleInfo?.status || cycleFullData?.status || "UPCOMING";
                        const hasAttendance = (cycleFullData?.attendance || []).length > 0;
                        const isDisabled = !hasAttendance || cycleStatus === "UPCOMING";

                        return {
                          value: cycleName,
                          label:
                            cycleName +
                            (cycleStatus === "UPCOMING"
                              ? " (Upcoming)"
                              : !hasAttendance
                              ? " (No Data)"
                              : ""),
                          disabled: isDisabled,
                        };
                      })}
                      onChange={(event) => handleCycleChange(event.target.value)}
                      width={220}
                    />
                  </ArgonBox>

                  {/* Student Dropdown */}
                  <ArgonBox>
                    <ArgonTypography
                      variant="body2"
                      fontWeight="medium"
                      color="text.primary"
                      mb={1}
                    >
                      Select Student
                    </ArgonTypography>
                    <Dropdown
                      label=""
                      value={student}
                      options={studentOptions}
                      onChange={(event) => handleStudentFilterChange(event.target.value)}
                      width={220}
                    />
                  </ArgonBox>

                  {/* Cycle Dropdown */}
                  <ArgonBox>
                    <ArgonTypography
                      variant="body2"
                      fontWeight="medium"
                      color="text.primary"
                      mb={1}
                    >
                      Filter by Type
                    </ArgonTypography>
                    <Dropdown
                      label=""
                      value={cycle}
                      options={cycleOptions}
                      onChange={(event) => handleCycleFilterChange(event.target.value)}
                      width={220}
                    />
                  </ArgonBox>

                  <ArgonBox
                    display="flex"
                    sx={{
                      alignSelf: { xs: "flex-start", sm: "flex-end" },
                      mt: { xs: 1.5, sm: 0 },
                    }}
                  >
                    <ArgonButton variant="outlined" color="secondary" onClick={handleResetFilters}>
                      Reset Filters
                    </ArgonButton>
                  </ArgonBox>
                </ArgonBox>
              </ArgonBox>

              {Boolean(stats.total) && (
                <ArgonBox display="flex" gap={2} flexWrap="wrap" mb={3} px={1}>
                  <Chip
                    label={`Total Sessions: ${stats.total}`}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip label={`Regular: ${stats.regular}`} color="success" variant="outlined" />
                  <Chip label={`Extra: ${stats.extra}`} color="info" variant="outlined" />
                  <Chip label={`Recovery: ${stats.recovery}`} color="warning" variant="outlined" />
                </ArgonBox>
              )}

              {/* Loading State */}
              {loading ? (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={5}>
                  <CircularProgress />
                </ArgonBox>
              ) : rows.length === 0 ? (
                <ArgonBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  p={4}
                  borderRadius="12px"
                  border="1px dashed #d1d1d1"
                >
                  <ArgonTypography variant="body2" color="text" fontWeight="medium">
                    No attendance records found for the selected filters.
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <>
                  {/* Attendance Card */}

                  <Card
                    sx={{
                      borderRadius: "20px",
                      background: "linear-gradient(135deg, #f5f7fa 0%, #eef1f5ff 100%)",
                      boxShadow: "0 15px 35px rgba(0,0,0,0.1)",
                      p: 4,
                      border: "1px solid #e0e0e0",
                      position: "relative",
                      overflow: "hidden",
                      mb: 4,
                    }}
                  >
                    {/* Decorative Background Element */}
                    <ArgonBox
                      sx={{
                        position: "absolute",
                        top: -50,
                        right: -50,
                        width: 150,
                        height: 150,
                        background: "linear-gradient(45deg, #efefefff, #f2f2f2ff)",
                        borderRadius: "50%",
                        opacity: 0.1,
                      }}
                    />

                    {/* Header */}
                    <ArgonBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={4}
                      flexWrap="wrap"
                    >
                      <ArgonBox display="flex" alignItems="center" gap={2}>
                        <ArgonBox
                          sx={{
                            background: "linear-gradient(45deg, #e2e2e2ff, #764ba2)",
                            borderRadius: "12px",
                            p: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                          }}
                        >
                          {/* <CalendarToday sx={{ color: "white", fontSize: "20px" }} /> */}
                        </ArgonBox>
                        <ArgonBox>
                          <ArgonTypography variant="h5" fontWeight="medium">
                            Attendance Overview
                          </ArgonTypography>
                          <ArgonTypography variant="body2" color="#6c757d">
                            Track your learning progress
                          </ArgonTypography>
                        </ArgonBox>
                      </ArgonBox>

                      <ArgonBox display="flex" gap={2} flexWrap="wrap">
                        <ArgonBox display="flex" alignItems="center" gap={1}>
                          <ArgonTypography variant="body2" fontWeight="medium" color="#6c757d">
                            Class Status:
                          </ArgonTypography>
                          <Chip
                            label={data.classStatus}
                            sx={{
                              fontSize: 12,
                              fontWeight: "bold",
                              color: "white !important",
                              background: "linear-gradient(45deg, #4CAF50, #45a049)",
                              borderRadius: "12px",
                              px: 2,
                              boxShadow: "0 4px 15px rgba(76, 175, 80, 0.3)",
                            }}
                          />
                        </ArgonBox>

                        <ArgonBox display="flex" alignItems="center" gap={1}>
                          <ArgonTypography variant="body2" fontWeight="medium" color="#6c757d">
                            Cycle Payment:
                          </ArgonTypography>
                          <Chip
                            label={data.cyclePaymentStatus}
                            sx={{
                              fontSize: 12,
                              fontWeight: "bold",
                              color: "white !important",
                              ...getPaymentStatusColor(data.cyclePaymentStatus),
                              borderRadius: "12px",
                              px: 2,
                            }}
                          />
                        </ArgonBox>
                      </ArgonBox>
                    </ArgonBox>

                    {/* Progress Section */}
                    <ArgonBox
                      sx={{
                        background: "white",
                        borderRadius: "16px",
                        p: 3,
                        mb: 3,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      }}
                    >
                      <ArgonBox display="flex" alignItems="center" gap={2} mb={2}>
                        {/* <TrendingUp sx={{ color: "#4CAF50", fontSize: "20px" }} /> */}
                        <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                          Session Progress
                        </ArgonTypography>
                      </ArgonBox>

                      {data.totalSessions === 0 ? (
                        <ArgonBox
                          sx={{
                            background: "#fff3cd",
                            borderRadius: "8px",
                            p: 2,
                            border: "1px solid #ffc107",
                          }}
                        >
                          <ArgonTypography variant="body2" color="warning" fontWeight="medium">
                            ⚠️ Total classes not configured. Current completed classes:{" "}
                            {data.completedSessions}
                          </ArgonTypography>
                        </ArgonBox>
                      ) : (
                        <>
                          <ArgonBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={2}
                          >
                            <ArgonTypography variant="body2" fontWeight="medium" color="#6c757d">
                              Sessions Completed: {data.completedSessions}/{data.totalSessions}
                            </ArgonTypography>
                            <ArgonTypography variant="h6" fontWeight="bold" color="#4CAF50">
                              {Math.round(progress)}%
                            </ArgonTypography>
                          </ArgonBox>

                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                              height: 12,
                              borderRadius: 6,
                              background: "#e0e0e0",
                              width: "100%",
                              "& .MuiLinearProgress-bar": {
                                background: "linear-gradient(45deg, #4CAF50, #45a049)",
                                borderRadius: 6,
                                width: `${progress}%`,
                              },
                            }}
                          />
                        </>
                      )}
                    </ArgonBox>

                    {/* Details Grid */}
                    <Grid container spacing={3} mb={3}>
                      <Grid item xs={12} md={4}>
                        <ArgonBox
                          sx={{
                            background: "white",
                            borderRadius: "12px",
                            p: 2.5,
                            textAlign: "center",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                            border: "1px solid #f0f0f0",
                          }}
                        >
                          <ArgonBox
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            gap={1}
                            mb={1}
                          >
                            {/* <Schedule sx={{ color: "#2196F3", fontSize: "18px" }} /> */}
                            <ArgonTypography variant="caption" fontWeight="medium" color="#6c757d">
                              Remaining Sessions
                            </ArgonTypography>
                          </ArgonBox>
                          <ArgonTypography variant="body2" fontWeight="medium" color="#2196F3">
                            {data.remainingSessions}
                          </ArgonTypography>
                        </ArgonBox>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <ArgonBox
                          sx={{
                            background: "white",
                            borderRadius: "12px",
                            p: 2.5,
                            textAlign: "center",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                            border: "1px solid #f0f0f0",
                          }}
                        >
                          <ArgonBox
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            gap={1}
                            mb={1}
                          >
                            {/* <PlayArrow sx={{ color: "#4CAF50", fontSize: "18px" }} /> */}
                            <ArgonTypography variant="caption" fontWeight="medium" color="#6c757d">
                              Start Date
                            </ArgonTypography>
                          </ArgonBox>
                          <ArgonTypography variant="body2" fontWeight="medium" color="#2c3e50">
                            {data.startDate}
                          </ArgonTypography>
                        </ArgonBox>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <ArgonBox
                          sx={{
                            background: "white",
                            borderRadius: "12px",
                            p: 2.5,
                            textAlign: "center",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                            border: "1px solid #f0f0f0",
                          }}
                        >
                          <ArgonBox
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            gap={1}
                            mb={1}
                          >
                            {/* <Stop sx={{ color: "#FF5722", fontSize: "18px" }} /> */}
                            <ArgonTypography variant="caption" fontWeight="medium" color="#6c757d">
                              End Date
                            </ArgonTypography>
                          </ArgonBox>
                          <ArgonTypography variant="body2" fontWeight="medium">
                            {data.endDate}
                          </ArgonTypography>
                        </ArgonBox>
                      </Grid>
                    </Grid>

                    {/* Remark Section */}
                    <ArgonBox
                      sx={{
                        background: "white",
                        borderRadius: "16px",
                        p: 3,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      }}
                      mb={2}
                    >
                      <ArgonBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={2}
                      >
                        <ArgonBox display="flex" alignItems="center" gap={2}>
                          {/* <Comment sx={{ color: "#9C27B0", fontSize: "20px" }} /> */}
                          <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                            Remarks
                          </ArgonTypography>
                        </ArgonBox>

                        <ArgonButton
                          variant="contained"
                          size="small"
                          color="primary"
                          onClick={handleDeleteClick}
                        >
                          Edit Remark
                        </ArgonButton>
                      </ArgonBox>

                      <ArgonTypography
                        variant="body2"
                        color="medium"
                        sx={{
                          background: "#f8f9fa",
                          borderRadius: "8px",
                          p: 1,
                          border: "1px solid #e9ecef",
                          fontStyle: "italic",
                        }}
                      >
                        {data.remark}
                      </ArgonTypography>
                    </ArgonBox>
                  </Card>
                  <Card>
                    <ArgonBox
                      sx={{
                        "& .MuiTableRow-root:not(:last-child)": {
                          "& td": {
                            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                              `${borderWidth[1]} solid ${borderColor}`,
                          },
                        },
                      }}
                    >
                      <Card
                        sx={{
                          borderRadius: "20px",
                          border: "1px solid #e0e0e0",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <ArgonBox p={3}>
                          <ArgonBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={2}
                          >
                            <ArgonTypography variant="h6" fontWeight="medium">
                              Attendance List
                            </ArgonTypography>
                            <ArgonButton
                              variant="gradient"
                              color="success"
                              size="medium"
                              onClick={handleMarkAttendanceOpen}
                              startIcon={<AddCircleIcon />}
                            >
                              Mark Attendance
                            </ArgonButton>
                          </ArgonBox>
                          <Table columns={columns} rows={rows} />
                        </ArgonBox>
                      </Card>
                    </ArgonBox>
                  </Card>
                </>
              )}
            </ArgonBox>

            {/* Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
              <DialogTitle>
                <ArgonTypography variant="h6" fontWeight="medium">
                  Edit Remark
                </ArgonTypography>
              </DialogTitle>

              <DialogContent dividers>
                <ArgonInput
                  fullWidth
                  multiline
                  minRows={4}
                  placeholder="Your Remark"
                  sx={{ mt: 1 }}
                />
              </DialogContent>

              <DialogActions sx={{ px: 3, py: 2 }}>
                <ArgonButton onClick={handleClose} variant="outlined" color="secondary">
                  Cancel
                </ArgonButton>
                <ArgonButton variant="contained" color="primary">
                  Save
                </ArgonButton>
              </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
              open={deleteDialogOpen}
              onClose={handleCloseDeleteDialog}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>
                <ArgonTypography variant="h6" fontWeight="medium">
                  Confirm Delete
                </ArgonTypography>
              </DialogTitle>

              <DialogContent dividers>
                <ArgonTypography variant="body2" color="text">
                  Are you sure you want to delete this attendance record? This action cannot be
                  undone.
                </ArgonTypography>
                {attendanceToDelete && (
                  <ArgonBox mt={2}>
                    <ArgonTypography variant="body2" fontWeight="medium">
                      Topic: {attendanceToDelete.topicCovered || "N/A"}
                    </ArgonTypography>
                    <ArgonTypography variant="body2" fontWeight="medium">
                      Date:{" "}
                      {attendanceToDelete.attendanceTime
                        ? new Date(attendanceToDelete.attendanceTime).toLocaleDateString()
                        : "N/A"}
                    </ArgonTypography>
                  </ArgonBox>
                )}
              </DialogContent>

              <DialogActions sx={{ px: 3, py: 2 }}>
                <ArgonButton
                  onClick={handleCloseDeleteDialog}
                  variant="outlined"
                  color="secondary"
                  disabled={loading}
                >
                  Cancel
                </ArgonButton>
                <ArgonButton
                  onClick={handleConfirmDelete}
                  variant="contained"
                  color="error"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : "Delete"}
                </ArgonButton>
              </DialogActions>
            </Dialog>

            <EditAttendanceModal
              open={editModalOpen}
              onClose={handleCloseEditModal}
              attendance={selectedAttendance}
              onUpdate={handleAttendanceUpdate}
            />

            <MarkAttendanceModal
              open={markAttendanceOpen}
              onClose={handleMarkAttendanceClose}
              onSubmit={handleSubmitMarkAttendance}
              loading={submittingAttendance}
              classId={classId}
            />
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
